import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

/** A student in one of an event's response buckets. */
export interface ApiEventResponder {
  user_id: number;
  first_name: string;
  last_name: string;
  grade_level: number;
}

export type EventResponse = 'confirmed' | 'declined';

/** A single event attendance record. */
export interface ApiAttendee {
  id: number;
  event_id: number;
  user_id: number;
  status: EventResponse;
}

/** Response shape for fetching the response buckets of a shared event. */
export interface EventAttendeesResponse {
  attending: ApiEventResponder[];
  notAttending: ApiEventResponder[];
  noResponse: ApiEventResponder[];
}

/** An event record returned from the API. */
export interface ApiEvent {
  id: number;
  title: string;
  day: string;
  start_time: string;
  end_time?: string;
  place?: string;
  description?: string;
  is_shared: boolean;
  user_id: number;
  EventGrades?: { grade_level: number }[];
  EventAttendees?: ApiAttendee[];
}

export function eventResponseOf(event: ApiEvent): EventResponse | null {
  return event.EventAttendees?.[0]?.status ?? null;
}

/** Payload shape for creating or updating an event. */
export interface EventPayload {
  title: string;
  day: string;
  start_time: string;
  end_time?: string;
  place?: string;
  description?: string;
  is_shared?: boolean;
  grades?: number[];
  groups?: number[];
  post_ids?: number[];
}

/** Service for all event CRUD and attendance operations. */
@Injectable({ providedIn: 'root' })
export class EventService {
  private http = inject(HttpClient);

  /** Fetches all events belonging to the current user. */
  getEvents(): Observable<ApiEvent[]> {
    return this.http.get<ApiEvent[]>('/api/events');
  }

  /** Creates a new event. */
  createEvent(payload: EventPayload): Observable<ApiEvent> {
    return this.http.post<ApiEvent>('/api/events', payload);
  }

  /** Updates an existing event by ID. */
  updateEvent(id: number, payload: Partial<EventPayload>): Observable<ApiEvent> {
    return this.http.patch<ApiEvent>(`/api/events/${id}`, payload);
  }

  /** Deletes an event by ID. */
  deleteEvent(id: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`/api/events/${id}`);
  }

  /** Fetches all events shared by counselors visible to the current student. */
  getSharedEvents(): Observable<ApiEvent[]> {
    return this.http.get<ApiEvent[]>('/api/events/shared');
  }

  setEventResponse(id: number, status: EventResponse): Observable<{ status: EventResponse }> {
    return this.http.put<{ status: EventResponse }>(`/api/events/${id}/response`, { status });
  }

  clearEventResponse(id: number): Observable<{ status: null }> {
    return this.http.delete<{ status: null }>(`/api/events/${id}/response`);
  }

  /** Fetches the attending and non-attending student lists for a shared event. */
  getEventAttendees(id: number): Observable<EventAttendeesResponse> {
    return this.http.get<EventAttendeesResponse>(`/api/events/${id}/attendees`);
  }
}
